import { ActionIcon, Avatar, Button, Group, Menu, Stack, Text, Tooltip, UnstyledButton, useMantineColorScheme } from '@mantine/core';
import { IconBuildingCog, IconLogout, IconMoon, IconSettings, IconSun, IconUserCircle } from '@tabler/icons-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@workos-inc/authkit-react';
import { BadgeBlock } from './BadgeBlock';
import React from 'react';
import { DEFAULT_PROFILE_PATH, PROFILE_ME_PATH } from '../lib/profileNav';
import { STAFF_URL } from '../staff/staffNav';
import { canAccessStaff } from '../staff/staffAccess';
import { isOrgAdmin } from '../lib/permissions';


type MenuAction = {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  hoverColor?: string;
};

export function UserMenu() {
  const auth = useAuth();
  const { colorScheme, setColorScheme } = useMantineColorScheme();

  const user = auth.user ?? { firstName: '', lastName: '', email: '' };

  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const onAccountPage = pathname === PROFILE_ME_PATH;
  const orgAdmin = isOrgAdmin(auth.permissions);
  const isDark = colorScheme === 'dark';
  const firstName = user.firstName ?? '';
  const lastName = user.lastName ?? '';
  const email = user.email ?? '';
  const initials = `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase() || 'U';
  const fullName = [firstName, lastName].filter(Boolean).join(' ') || email || 'You';
  const toggleTheme = () => setColorScheme(isDark ? 'light' : 'dark');

  const handleSignOut = () => {
    auth.signOut();
  };

  const isStaff = canAccessStaff(auth.permissions);

  const menuActions: MenuAction[] = [
    // Offering the page you are already on would only re-navigate. The query
    // string rides along so a product's ?from= survives the hop from an admin page.
    ...(onAccountPage ? [] : [{
      label: 'My account',
      icon: <IconUserCircle size={16} stroke={1.8} />,
      onClick: () => navigate({ pathname: PROFILE_ME_PATH, search }),
      hoverColor: 'var(--accent-text)',
    }]),
    ...(orgAdmin
      ? [{
        label: 'Manage organisation',
        icon: <IconSettings size={16} stroke={1.8} />,
        onClick: () => navigate(DEFAULT_PROFILE_PATH),
        hoverColor: 'var(--accent-text)',
      }]
      : []),
    ...(isStaff ? [{
      label: 'Cloudhouse staff',
      icon: <IconBuildingCog size={16} stroke={1.8} />,
      onClick: () => navigate(STAFF_URL),
      hoverColor: 'var(--accent-text)',
    }] : []),
    {
      label: 'Sign out',
      icon: <IconLogout size={16} stroke={1.8} />,
      onClick: handleSignOut,
      hoverColor: 'var(--status-danger)',
    },
  ];

  return (
    <Group gap={12} align="center" wrap="nowrap">
      <Tooltip label="Toggle theme" position="bottom" withArrow>
        <ActionIcon
          variant="subtle"
          onClick={toggleTheme}
          aria-label="Toggle theme"
          size={34}
          style={{
            border: '1px solid var(--border-default)',
            '--ai-color': 'var(--text-muted)',
            '--ai-hover-color': 'var(--accent-text)',
            '--ai-hover': 'rgba(81,100,112,0.03)',
          }}
        >
          {isDark ? <IconSun size={17} stroke={2} /> : <IconMoon size={17} stroke={2} />}
        </ActionIcon>
      </Tooltip>

      <Menu width={288} position="bottom-end" offset={10} withArrow={false} shadow="lg">
        <Menu.Target>
          <UnstyledButton
            aria-label="Open user menu"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              padding: '0',
            }}
          >
            <Stack gap={2} align="flex-start">
              <Text size="sm" fw={600} c="var(--text-default)">{fullName}</Text>
              {auth.role && (
                <BadgeBlock color="var(--accent-text)" >
                  {auth.role}
                </BadgeBlock>
              )}
            </Stack>

            <Avatar size={34} radius="xl"
              style={{
                '--avatar-bg': 'linear-gradient(135deg,#516470,#0D2839)',
                '--avatar-bd': 'none'
              }}
            >
              <Text size="xs" fw={700} c={'white'}>{initials}</Text>
            </Avatar>
          </UnstyledButton>
        </Menu.Target>

        <Menu.Dropdown
          w={224}
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-default)',
            overflow: 'hidden',
            padding: 0,
          }}

        >
          {menuActions.map((action, index) => (
            <React.Fragment key={action.label}>
              <Button
                fullWidth
                variant="subtle"
                justify="flex-start"
                leftSection={action.icon}
                onClick={action.onClick}
                radius={0}
                style={{
                  '--button-color': 'var(--text-default)',
                  '--button-hover-color': action.hoverColor,
                  '--button-hover': 'color-mix(in srgb, var(--button-hover-color), transparent 90%)',
                }}
              >
                {action.label}
              </Button>
              {index < menuActions.length - 1 && <Menu.Divider my={2} style={{ borderColor: 'var(--border-subtle)' }} />}
            </React.Fragment>
          ))}
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
}
